import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Linkedin, Twitter } from 'lucide-react';
import SecureReveal from './SecureReveal';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { label: 'Création de sites', path: '/services/creation' },
      { label: 'Refonte web', path: '/services/refonte' },
      { label: 'E-commerce', path: '/services/ecommerce' },
      { label: 'Maintenance', path: '/services/maintenance' }
    ],
    marketing: [
      { label: 'SEO Local', path: '/marketing/seo-local' },
      { label: 'Google Ads', path: '/marketing/google-ads' },
      { label: 'Social Ads', path: '/marketing/social-ads' },
      { label: 'Audit SEO', path: '/marketing/audit' }
    ],
    company: [
      { label: 'À propos', path: '/agence' },
      { label: 'Méthodologie', path: '/methodologie' },
      { label: 'Contact', path: '/contact' },
      { label: 'Simulateur', path: '/simulateur' }
    ],
    legal: [
      { label: 'Mentions légales', path: '/mentions-legales' },
      { label: 'Confidentialité', path: '/confidentialite' },
      { label: 'CGV', path: '/cgv' }
    ]
  };

  return (
    <footer className="border-t mt-20 pb-20 md:pb-0" style={{
      background: '#0a0a0c',
      borderColor: 'rgba(255, 255, 255, 0.08)'
    }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-20 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-center md:text-left">
          {/* Brand Column */}
          <div className="space-y-4 flex flex-col items-center md:items-start">
            <Link to="/" className="inline-block group relative">
              <div className="absolute inset-0 bg-white blur-2xl opacity-80"></div>
              <div className="relative bg-white rounded-xl p-[3px] shadow-2xl shadow-white/25">
                <img
                  src="/assets/logo.png"
                  alt="WEB YOU FIRST"
                  className="h-14 transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </Link>
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Votre partenaire digital dans les Yvelines. Création de sites web performants et stratégies marketing sur-mesure.
            </p>

            {/* Contact Info - Protégé avec SecureReveal */}
            <div className="space-y-3 pt-4">
              <div className="flex items-center space-x-3 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                <SecureReveal
                  type="phone"
                  value="06 XX XX XX XX"
                  icon={Phone}
                  label="Voir le téléphone"
                />
              </div>
              <div className="flex items-center space-x-3 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                <SecureReveal
                  type="email"
                  value="contact@webyoufirst.fr"
                  icon={Mail}
                  label="Voir l'email"
                />
              </div>
              <div className="flex items-center space-x-3 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                <MapPin size={16} className="text-[#00cec9]" />
                <span>Siège social : Bois d'Arcy, Yvelines (78)</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4 pt-4 justify-center md:justify-start">
              <a href="#" className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
                aria-label="Facebook"
              >
                <Facebook size={18} style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
              </a>
              <a href="#" className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
                aria-label="LinkedIn"
              >
                <Linkedin size={18} style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
              </a>
              <a href="#" className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
                aria-label="Twitter"
              >
                <Twitter size={18} style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm transition-colors duration-300 hover:text-[#00cec9]"
                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Marketing Column */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">Marketing</h3>
            <ul className="space-y-3">
              {footerLinks.marketing.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm transition-colors duration-300 hover:text-[#00cec9]"
                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">Entreprise</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm transition-colors duration-300 hover:text-[#00cec9]"
                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
          style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}
        >
          <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            © {currentYear} WEB YOU FIRST. Tous droits réservés.
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm transition-colors duration-300 hover:text-[#00cec9]"
                style={{ color: 'rgba(255, 255, 255, 0.6)' }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
